import { cva, type VariantProps } from 'class-variance-authority'
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'

const alertVariants = cva(
  'flex gap-3 rounded-control border p-3.5 text-sm [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      tone: {
        info: 'border-info-500/25 bg-info-50 text-info-700',
        success: 'border-success-500/25 bg-success-50 text-success-700',
        warning: 'border-warning-500/30 bg-warning-50 text-warning-700',
        danger: 'border-danger-500/25 bg-danger-50 text-danger-700',
      },
    },
    defaultVariants: { tone: 'info' },
  },
)

const icons = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: XCircle }

interface AlertProps extends VariantProps<typeof alertVariants> {
  title?: ReactNode
  children?: ReactNode
  action?: ReactNode
  className?: string
}

export function Alert({ tone = 'info', title, children, action, className }: AlertProps) {
  const Icon = icons[tone ?? 'info']
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={cn(alertVariants({ tone }), className)}>
      <Icon aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={cn(title && 'mt-0.5', 'opacity-90')}>{children}</div> : null}
      </div>
      {action}
    </div>
  )
}
